import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, FunnelIcon } from '@heroicons/react/24/solid';
import { formatDate } from 'shared_items/formatConventions';
import DropdownCustom from '../../../moduleComponents/DropdownCustom';
import DropdownTableStartLevelChip from '../Detailed/DropdownTableStartLevelChip';
import AssetsHierarchyBody from '../Common/AssetsHierarchyBody';
import ModalAssetEdit from '../Common/ModalAssetEdit';
import { useAssetsSnapshotTable } from '../../viewServices/use_assets_snapshot_table';
import { TABLE_START_LEVEL } from '../../viewServices/assets_hierarchy';

const ASSET_TYPE_FILTER_OPTIONS = [
  { value: 'deposit', label: 'Deposit' },
  { value: 'tradeable', label: 'Tradeable' },
];

const LEVEL_OPTIONS = [
  { level: TABLE_START_LEVEL.L1, label: 'Category', title: 'Table starting at category (L1)' },
  { level: TABLE_START_LEVEL.L2, label: 'Sub-Category', title: 'Table starting at sub-category (L2)' },
  { level: TABLE_START_LEVEL.L3, label: 'Asset', title: 'Assets only (L3)' },
  { level: TABLE_START_LEVEL.LOCATION, label: 'Location', title: 'Group by location' },
];

const WorkflowAssetsTable = () => {
  const table = useAssetsSnapshotTable();
  const [assetToEdit, setAssetToEdit] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isSnapshotDropdownOpen, setIsSnapshotDropdownOpen] = useState(false);
  const [snapshotMenuStyle, setSnapshotMenuStyle] = useState(null);
  const snapshotDropdownRef = useRef(null);
  const snapshotMenuRef = useRef(null);
  const assetTypeDropdownRef = useRef(null);

  useEffect(() => {
    if (!isSnapshotDropdownOpen) return undefined;
    const updateMenuPosition = () => {
      const el = snapshotDropdownRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = Math.max(rect.width, 140);
      setSnapshotMenuStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.right - width,
        width,
        zIndex: 50,
        maxHeight: 192,
      });
    };
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isSnapshotDropdownOpen]);

  useEffect(() => {
    if (!isSnapshotDropdownOpen) return undefined;
    const handleClickOutside = (e) => {
      const inButton = snapshotDropdownRef.current?.contains(e.target);
      const inMenu = snapshotMenuRef.current?.contains(e.target);
      if (!inButton && !inMenu) setIsSnapshotDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSnapshotDropdownOpen]);

  const hasActiveFilter = table.showOnlyInSnapshot
    || table.hideInactive
    || (table.assetNameSearch?.trim() ?? '') !== ''
    || Boolean(table.assetTypeFilter);

  const onTypeSelect = (value) => {
    const next = value && typeof value === 'object' ? value.value : value;
    table.setAssetTypeFilter(next || '');
  };

  return (
    <>
      {table.error && (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {table.error}
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/50">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium text-gray-800">Assets</span>
            {!table.loading && (
              <span className="text-xs text-gray-400">({table.displayedAssets.length})</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <div className="sm:hidden">
              <DropdownTableStartLevelChip
                value={table.tableStartLevel}
                onChange={table.onTableStartLevelChange}
              />
            </div>
            <div
              className="hidden rounded-full bg-gray-100 p-0.5 sm:inline-flex"
              role="group"
              aria-label="Nível inicial da tabela"
            >
              {LEVEL_OPTIONS.map(({ level, label, title }, index) => {
                const selected = table.tableStartLevel === level;
                return (
                  <button
                    key={level}
                    type="button"
                    title={title}
                    onClick={() => table.onTableStartLevelChange(level)}
                    className={`px-2.5 py-1 text-xs font-medium transition-all ${
                      index === 0 ? 'rounded-l-full' : ''
                    } ${index === LEVEL_OPTIONS.length - 1 ? 'rounded-r-full' : ''} ${
                      selected ? 'bg-green-500 text-white' : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="relative" ref={snapshotDropdownRef}>
              <button
                type="button"
                onClick={() => setIsSnapshotDropdownOpen((o) => !o)}
                className="flex max-w-[11rem] items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                title="Select snapshot"
              >
                <span className="truncate">
                  {table.snapshotDate ? formatDate(table.snapshotDate, 'display') : 'Select snapshot'}
                </span>
                <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform ${isSnapshotDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isSnapshotDropdownOpen && snapshotMenuStyle && createPortal(
                <div
                  ref={snapshotMenuRef}
                  style={snapshotMenuStyle}
                  className="overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                >
                  {table.snapshotList.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        table.onSelectSnapshot(s.id);
                        setIsSnapshotDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                        table.selectedSnapshotId === s.id ? 'bg-green-50 font-medium text-green-800' : 'text-gray-700'
                      }`}
                    >
                      {formatDate(s.snapshot_date, 'display')}
                    </button>
                  ))}
                  {table.snapshotList.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">No snapshots</div>
                  )}
                </div>,
                document.body
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsFilterExpanded((f) => !f)}
              className={`rounded-lg border p-1.5 transition-colors ${
                isFilterExpanded || hasActiveFilter
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              aria-label="Toggle filters"
            >
              <FunnelIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          className={`overflow-hidden border-b border-gray-200 bg-white transition-all duration-200 ease-in-out ${
            isFilterExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-wrap items-center gap-3 px-3 py-2">
            <input
              type="search"
              value={table.assetNameSearch}
              onChange={(e) => table.setAssetNameSearch(e.target.value)}
              placeholder="Asset name"
              className="h-7 min-w-[140px] max-w-[180px] rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-800 placeholder:text-gray-400 focus:border-gray-300 focus:outline-none"
              aria-label="Filter by asset name"
            />
            <div className="min-w-[130px]" ref={assetTypeDropdownRef}>
              <DropdownCustom
                options={ASSET_TYPE_FILTER_OPTIONS}
                selectedValue={table.assetTypeFilter || ''}
                onSelect={onTypeSelect}
                placeholder="All types"
                width="min-w-[130px]"
                usePortal
                portalRef={assetTypeDropdownRef}
                showAllOption
                allOption={{ value: '', label: 'All types' }}
                showRadio
              />
            </div>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={table.hideInactive}
                onChange={(e) => table.setHideInactive(e.target.checked)}
                className="h-3 w-3 accent-green-500"
              />
              Hide inactive
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                role="switch"
                aria-checked={table.showOnlyInSnapshot}
                aria-label={table.showOnlyInSnapshot ? 'Only in snapshot' : 'All assets'}
                title={table.showOnlyInSnapshot ? 'Show only assets with value in current snapshot' : 'Show all assets'}
                onClick={() => table.onToggleShowOnlyWithValue(!table.showOnlyInSnapshot)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-0 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  table.showOnlyInSnapshot ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 shrink-0 transform rounded-full bg-white shadow ring-0 transition-transform ${
                    table.showOnlyInSnapshot ? 'ml-0.5 translate-x-4' : 'translate-x-0.5'
                  }`}
                  aria-hidden
                />
              </button>
              <span className="select-none text-xs text-gray-600">
                {table.showOnlyInSnapshot ? 'only in snapshot' : 'all assets'}
              </span>
            </div>
          </div>
        </div>

        <AssetsHierarchyBody
          loading={table.loading}
          filteredAssets={table.displayedAssets}
          hierarchy={table.hierarchy}
          sortedLevel1={table.sortedLevel1}
          snapshotMap={table.snapshotMap}
          tableStartLevel={table.tableStartLevel}
          onEditAsset={setAssetToEdit}
          maxHeightClass="max-h-[32vh]"
        />
      </div>

      {assetToEdit && (
        <ModalAssetEdit
          selectedAsset={assetToEdit}
          onClose={() => setAssetToEdit(null)}
          onSuccess={table.refreshFull}
        />
      )}
    </>
  );
};

export default WorkflowAssetsTable;
